import * as React from 'react';
import './AccountCard.scss';
import AccountInfoHeader from '../AccountInfoHeader';
import AccountInfoFooter from '../AccountInfoFooter';
import CreditCardInfoModel from 'src/model/creditCardInfo.model';
import EditOrDeleteCreditCardService from 'src/services/creditCard/EditOrDeleteCreditCardService';
import addCreditCardService from 'src/services/creditCard/AddCreditCardService';
import CreditCardIframeModel from 'src/model/creditCardIframe.model';
import EditForm from './components/EditForm';
import CreditCard from 'src/components/common/CreditCard';
import Modal from 'src/components/common/Modal/Modal';
import SlideMenu from 'src/components/common/SlideMenu';
import Accordion from 'src/components/common/Accordion/Accordion';
import DeleteCreditCardConfirmation from './components/DeleteCreditCardConfirmation';
import useMobile from 'src/hooks/useMobile';
import i18n from 'src/translation/i18n';

export function AccountCard(mainProps: any) {
  const { t } = mainProps;
  const isMobile = useMobile();

  const [creditCardsInfo, setCreditCardsInfo] = React.useState<
    CreditCardInfoModel[]
  >([]);

  const [
    creditCardIframeUrlandSessionID,
    setCreditCardIframeUrlandSessionID
  ] = React.useState<CreditCardIframeModel | null>(null);

  const [showIframe, setShowIframe] = React.useState<boolean>(false);
  const [showEditForm, setShowEditForm] = React.useState<boolean>(false);

  const [showIframeMobile, setShowIframeMobile] = React.useState<boolean>(
    false
  );
  const [showEditFormMobile, setShowEditFormMobile] = React.useState<boolean>(
    false
  );

  const [
    selectedCreditCardToEdit,
    setSelectedCreditCardToEdit
  ] = React.useState<null | CreditCardInfoModel>(null);

  const [
    selectedCreditCardToDelete,
    setSelectedCreditCardToDelete
  ] = React.useState<null | CreditCardInfoModel>(null);

  const [showRemoveCCError, setshowRemoveCCError] = React.useState<boolean>(
    false
  );

  React.useEffect(() => {
    getCreditCards();
  }, []);

  const getCreditCards = () => {
    EditOrDeleteCreditCardService.getSavedCreditCardsInfo().then(response => {
      setCreditCardsInfo(response);
    });
  };

  const onCreditCardSuccess = () => {
    const currentSessionID = creditCardIframeUrlandSessionID.sessionID;
    addCreditCard(currentSessionID);
  };

  const onCreditCardCancel = () => {
    setShowIframe(false);
    setShowEditForm(false);
  };

  const displayIframe = () => {
    addCreditCardService
      .getCreditCardIframeUrlAndSessionId()
      .then(response => {
        setCreditCardIframeUrlandSessionID(response);
        setShowIframe(true);
      })
      .catch(error => console.error(error));
  };

  const displayIframeMobile = () => {
    addCreditCardService
      .getCreditCardIframeUrlAndSessionId()
      .then(response => {
        setCreditCardIframeUrlandSessionID(response);
        setShowIframeMobile(true);
      })
      .catch(error => console.error(error));
  };

  const addCreditCard = (sessionID: string) => {
    addCreditCardService
      .addCreditCard(sessionID)
      .then(() => {
        getCreditCards();
        setShowIframe(false);
        setShowIframeMobile(false);
        setShowEditForm(false);
        setShowEditFormMobile(false);
      })
      .catch(error => console.log(error));
  };

  const displayEditForm = () => {
    setShowIframe(false);
    setShowEditForm(true);
  };

  const displayEditFormMobile = () => {
    setShowIframeMobile(false);
    setShowEditFormMobile(true);
  };

  const removeCreditCard = (
    creditCardToken: string,
    creditCardType: string
  ) => {
    EditOrDeleteCreditCardService.deleteCreditCardsInfo({
      cardToken: creditCardToken,
      cardType: creditCardType
    })
      .then(() => {
        setSelectedCreditCardToDelete(null);
        getCreditCards();
      })
      .catch(() => {
        setshowRemoveCCError(true);
      });
  };

  const closeCreditCartAlert = () => {
    setshowRemoveCCError(false);
  };

  const renderAccordion = (data: CreditCardInfoModel[]) => {
    if (!data || (data && data.length === 0)) {
      return (
        <div className="rp-accountcard--card-table-empty">
          <p>{t('accountCreditCards.noCardsFound')}</p>
        </div>
      );
    } else {
      return (
        <>
          <Accordion
            showHandleCollapse={false}
            allowMultipleOpen={true}
            startExpanded={false}
            expandAllChild={null}
          >
            {renderAccordionData(data)}
          </Accordion>
        </>
      );
    }
  };

  const renderAccordionData = (data: CreditCardInfoModel[]) => {
    return data.map((item: CreditCardInfoModel, idx: number) => {
      return (
        <AccordionSection
          identifier={idx}
          key={idx}
          header={
            <AccordionSectionHeader
              cardType={item.cardType}
              cardToken={item.cardToken}
              expirationDate={item.expirationDate}
            />
          }
        >
          <AccordionSectionExpandable
            holderAddress1={item.holderAddress1}
            holderAddress2={item.holderAddress2}
            city={item.city}
            holderZipCode={item.holderZipCode}
            holderCountry={item.holderCountry}
            holderName={item.holderName}
            currentCard={item}
          />
        </AccordionSection>
      );
    });
  };

  const AccordionSection = (props: {
    identifier: number;
    header: any;
    children?: any;
  }) => {
    return <div>{props.children}</div>;
  };

  const AccordionSectionHeader = (props: {
    cardType: string;
    cardToken: string;
    expirationDate: string;
  }) => {
    return (
      <>
        <div className="rp-accordion-section--header-type-number">
          <div className="rp-accordion-section-title">
            {t('accountCreditCards.tableCreditCard')}
          </div>
          <div className="rp-accordion-section-data">{`${
            props.cardType
          } - ${props.cardToken.substring(12)}`}</div>
        </div>
        <div className="rp-accordion-section--header-expiration-date">
          <div className="rp-accordion-section-title">
            {t('accountCreditCards.expirationDate')}
          </div>
          <div className="rp-accordion-section-data">
            {`${props.expirationDate.substring(
              5
            )}/${props.expirationDate.substring(0, 4)}`}
          </div>
        </div>
      </>
    );
  };

  const AccordionSectionExpandable = (props: {
    holderAddress1: string;
    holderAddress2: string;
    city: string;
    holderZipCode: string;
    holderCountry: string;
    holderName: string;
    currentCard: CreditCardInfoModel;
  }) => {
    return (
      <>
        <div className="rp-accordion-section--expandable-info">
          <div className="rp-accordion-section-title">
            {t('accountCreditCards.tableBillingAddress')}
          </div>
          <div className="rp-accordion-section-data">
            {`${props.holderAddress1}${
              props.holderAddress2 ? ` ${props.holderAddress2}` : ''
            }, ${props.city}, ${props.holderZipCode}. ${
              props.holderCountry === 'USA'
                ? 'United States'
                : props.holderCountry
            }`}
          </div>
          <div className="rp-accordion-section-title">
            {t('accountCreditCards.tableNameOnCard')}
          </div>
          <div className="rp-accordion-section-data">{props.holderName}</div>
        </div>
        <div className="rp-accordion-section--expandable-actions">
          <div className="rp-accordion-section-title">
            {t('accountCreditCards.tableActions')}
          </div>
          <div className="rp-accordion-section-data">
            <span
              onClick={() => setSelectedCreditCardToDelete(props.currentCard)}
              className="rpds-trash"
            />
            <span
              onClick={() => {
                displayEditFormMobile();
                setSelectedCreditCardToEdit(props.currentCard);
              }}
              className="rpds-edit"
            />
          </div>
        </div>
      </>
    );
  };

  const renderTable = (data: CreditCardInfoModel[]) => {
    if (!data || (data && data.length === 0)) {
      return (
        <div className="rp-accountcard--card-table-empty">
          <p>{t('accountCreditCards.noCardsFound')}</p>
        </div>
      );
    } else {
      return (
        <>
          <div className="rp-accountcard--tableheader-container">
            <p className="rp-accountcard--tableheader-title rp-accountcard--tablepercentage-17">
              {t('accountCreditCards.tableCreditCard')}
            </p>
            <p className="rp-accountcard--tableheader-title rp-accountcard--tablepercentage-14">
              {t('accountCreditCards.tableExpiration')}
            </p>
            <p className="rp-accountcard--tableheader-title rp-accountcard--tablepercentage-29">
              {t('accountCreditCards.tableBillingAddress')}
            </p>
            <p className="rp-accountcard--tableheader-title rp-accountcard--tablepercentage-20">
              {t('accountCreditCards.tableNameOnCard')}
            </p>
            <p className="rp-accountcard--tableheader-title rp-accountcard--tablepercentage-20">
              {t('accountCreditCards.tableActions')}
            </p>
          </div>
          <div className={`rp-accountcard--tableinfo-container`}>
            {renderTableData(data)}
          </div>
        </>
      );
    }
  };

  const renderTableData = (data: CreditCardInfoModel[]) => {
    return data.map((item: CreditCardInfoModel, idx: number) => {
      return (
        <div key={idx} className="rp-accountcard--tableinfo-row">
          <p className="rp-accountcard--tableinfo-data rp-accountcard--tablepercentage-17">
            <div>{`${item.cardType} - ${item.cardToken.substring(12)}`}</div>
          </p>
          <p className="rp-accountcard--tableinfo-data rp-accountcard--tablepercentage-14">
            {`${item.expirationDate.substring(
              5
            )}/${item.expirationDate.substring(0, 4)}`}
          </p>
          <p className="rp-accountcard--tableinfo-data rp-accountcard--tablepercentage-29">
            <div>{`${item.holderAddress1}${
              item.holderAddress2 ? ` ${item.holderAddress2}` : ''
            }, ${item.city}, ${item.holderZipCode}`}</div>
            <div>
              {item.holderCountry === 'USA'
                ? 'United States'
                : item.holderCountry}
            </div>
          </p>
          <p className="rp-accountcard--tableinfo-data rp-accountcard--tablepercentage-20">
            <div>{item.holderName}</div>
          </p>
          <p className="rp-accountcard--tableinfo-data rp-accountcard--tablepercentage-20">
            <button
              className="rp-accountcard-button secondary-button"
              onClick={() => setSelectedCreditCardToDelete(item)}
            >
              {t('common.removeAction')}
            </button>
            <button
              className="rp-accountcard--edit-button rp-accountcard-button primary-button"
              onClick={() => {
                displayEditForm();
                setSelectedCreditCardToEdit(item);
              }}
            >
              {t('common.editActionTxt')}
            </button>
          </p>
        </div>
      );
    });
  };

  return (
    <>
      <div className="rp-accountcard">
        <AccountInfoHeader />
        <div className="rp-accountcard--content">
          <div className="rp-accountcard--content-header">
            <div className="rp-accountcard--title-container">
              <h1 className="rp-accountcard--title">
                {t('accountCreditCards.manageCardTitile')}
              </h1>
            </div>
            <div className="rp-accountcard--add-button-container">
              <button
                className="rp-accountcard--add-button primary-button"
                onClick={isMobile ? displayIframeMobile : displayIframe}
              >
                {t('accountCreditCards.addCreditCardActionTxt', {
                  interpolation: { escapeValue: false }
                })}
              </button>
            </div>
          </div>
          {isMobile ? (
            <div className="rp-accountcard--accordion-container">
              {renderAccordion(creditCardsInfo)}
            </div>
          ) : (
            <div className="rp-accountcard--table-container">
              {renderTable(creditCardsInfo)}
            </div>
          )}
        </div>
        <AccountInfoFooter />
      </div>
      {showIframe && (
        <div className="rp-accountcard--add-form-modal-container">
          <Modal
            handleCloseModal={() => setShowIframe(false)}
            styles={{
              display: 'flex',
              justifyContent: 'center',
              background: 'white',
              textAlign: 'left',
              color: 'black'
            }}
            showClose={false}
          >
            <div className="rp-accountcard--iframe-container">
              <div className="rp-accountcard--iframe-close">
                <i
                  className="rpds-close close-modal"
                  onClick={() => setShowIframe(false)}
                />
              </div>
              <h1 className="rp-accountcard--iframe-title">
                {t('accountCreditCards.enterCreditCardDetails')}
              </h1>
              <CreditCard
                show={
                  showIframe && creditCardIframeUrlandSessionID.url !== null
                }
                onSuccess={onCreditCardSuccess}
                onCancel={onCreditCardCancel}
                creditCardUrl={creditCardIframeUrlandSessionID.url}
              />
            </div>
          </Modal>
        </div>
      )}
      <div className="rp-accountcard--add-form-container-mobile">
        <SlideMenu
          mounted={showIframeMobile}
          goBackTitle={i18n.t('common.back')}
          level={2 + 30}
          handleCloseMenu={() => setShowIframeMobile(false)}
        >
          <div className="rp-accountcard--iframe-container">
            <h1 className="rp-accountcard--iframe-title">
              {t('accountCreditCards.addCreditCardActionTxt', {
                interpolation: { escapeValue: false }
              })}
            </h1>
            {showIframeMobile && (
              <CreditCard
                show={showIframeMobile}
                onSuccess={onCreditCardSuccess}
                onCancel={onCreditCardCancel}
                creditCardUrl={creditCardIframeUrlandSessionID.url}
              />
            )}
          </div>
        </SlideMenu>
      </div>
      {showEditForm && (
        <div className="rp-accountcard--edit-form-modal-container">
          <Modal
            handleCloseModal={() => setShowEditForm(false)}
            styles={{
              display: 'flex',
              justifyContent: 'center',
              maxHeight: '80vh',
              background: 'white',
              textAlign: 'left',
              color: 'black'
            }}
            showClose={false}
          >
            <EditForm
              onCancel={() => {
                setShowEditForm(false);
                setShowIframe(false);
                setSelectedCreditCardToEdit(null);
              }}
              onSubmit={() => {
                setShowEditForm(false);
                setShowIframe(false);
                getCreditCards();
              }}
              fields={selectedCreditCardToEdit}
            />
          </Modal>
        </div>
      )}
      <div className="rp-accountcard--edit-form-container-mobile">
        <SlideMenu
          mounted={showEditFormMobile}
          goBackTitle={i18n.t('common.back')}
          level={2 + 30}
          handleCloseMenu={() => setShowEditFormMobile(false)}
        >
          <EditForm
            onCancel={() => {
              setShowEditFormMobile(false);
              setSelectedCreditCardToEdit(null);
            }}
            onSubmit={() => {
              setShowEditFormMobile(false);
              getCreditCards();
            }}
            fields={selectedCreditCardToEdit}
          />
        </SlideMenu>
      </div>
      {selectedCreditCardToDelete && (
        <Modal
          handleCloseModal={() => setSelectedCreditCardToDelete(null)}
          showClose={false}
        >
          <DeleteCreditCardConfirmation
            showAlert={showRemoveCCError}
            onCloseAlert={closeCreditCartAlert}
            creditCardToken={selectedCreditCardToDelete.cardToken}
            onDelete={() =>
              removeCreditCard(
                selectedCreditCardToDelete.cardToken,
                selectedCreditCardToDelete.cardType
              )
            }
            onCancel={() => setSelectedCreditCardToDelete(null)}
          />
        </Modal>
      )}
    </>
  );
}
